import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import HomeGate from "./pages/HomeGate";
import TransportUIGate from "./components/TransportUIGate";
import Settings from "./pages/Settings";
import Editor from "./pages/Editor";
// Reports removed per GDPR requirements
// import Reports from "./pages/Reports";
import ReportsHub from "./pages/ReportsHub";
import CompanyCarHub from "./pages/CompanyCarHub";
import MileageTracker from "./pages/MileageTracker";
import Areas from "./pages/Areas";
import StaffologyPortal from "./pages/StaffologyPortal";
import GroupAbsenceCalendar from "./pages/GroupAbsenceCalendar";
import CRMPortal from "./pages/CRMPortal";
import PhotoOptimization from "./pages/PhotoOptimization";
import TransportAutoSaveDocs from "./pages/TransportAutoSaveDocs";
import TransportAutoSaveDocsV2 from "./pages/TransportAutoSaveDocsV2";
import SalesDashboard from "./pages/SalesDashboard";
// CustomerManagement removed per GDPR requirements
// import CustomerManagement from "./pages/CustomerManagement";
import Projects from "./pages/Projects";
import PICounts from "./pages/PICounts";
import PICountDetail from "./pages/PICountDetail";
import PIReportViewer from "./pages/PIReportViewer";
import PICountsWarehouse from "./pages/PICountsWarehouse";
import DiscrepancyReport from "./pages/DiscrepancyReport";
import AdjustmentReport from "./pages/AdjustmentReport";
import GoodsIn from "./pages/GoodsIn";
import GoodsInWarehouse from "./pages/GoodsInWarehouse";
import CrossDeploymentBackup from "./pages/CrossDeploymentBackup";
// Budget removed per GDPR requirements
// import Budget from "./pages/Budget";
import SalesTracker from "./pages/SalesTracker";
import ErrorLog from "./pages/ErrorLog";
import M45Hub from "./pages/M45Hub";
import Proforma from "./pages/Proforma";
import ProformaCreate from "./pages/ProformaCreate";
import ProformaPreview from "./pages/ProformaPreview";
import ProformaView from "./pages/ProformaView";
import Quotes from "./pages/Quotes";
import QuoteCreate from "./pages/QuoteCreate";
import QuotePreview from "./pages/QuotePreview";
import QuoteView from "./pages/QuoteView";
import StockTransfer from "./pages/StockTransfer";
import B29StockTransfer from "./pages/B29StockTransfer";
import POExpenses from "./pages/POExpenses";
import ManageCallsPage from "./pages/ManageCallsPage";
import ProjectTrackingPage from "./pages/ProjectTrackingPage";
import SpellersPage from "./pages/SpellersPage";
import SpellersProjectDetail from "./pages/SpellersProjectDetail";
import PriceIncreases from "./pages/PriceIncreases";
import StockManagement from "./pages/StockManagement";
import AutomationsDashboard from "./pages/AutomationsDashboard";
import SDPCRMPage from "./pages/SDPCRMPage";
import SalesHubPage from "./pages/SalesHubPage";
import BIReportsPage from "./pages/BIReportsPage";
import OAuthConsent from "./pages/OAuthConsent";
// InterviewNotes is now a modal within the Tools dropdown (no standalone page)
// import Files from "./pages/Files";
import ErrorBoundary from "./components/ErrorBoundary";
import InstallPrompt from "./components/InstallPrompt";
import VersionUpdateBanner from "./components/VersionUpdateBanner";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./providers/ThemeProvider";
import { BranchProvider } from "./context/BranchContext";
import { ZohoSessionProvider } from "./contexts/ZohoSessionContext";
import { UIPreviewProvider } from "./contexts/UIPreviewContext";
import { DashboardV3Provider, DashboardV3Tag, useDashboardV3Test } from "./contexts/DashboardV3Context";
import { ActivityBarProvider } from "./contexts/ActivityBarContext";
import { TransportReturnProvider } from "./contexts/TransportReturnContext";
import LimitedRoutes from "./limited/LimitedRoutes";
import V3MainGate from "./v3/V3Shell";
import ZohoLoginGateModal from "./components/ZohoLoginGateModal";
import ActivityModalHost from "./components/ActivityModalHost";
import { initDesktopZoomFix } from "./utils/desktopZoomFix";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Laptop/desktop screen-size zoom fix — see utils/desktopZoomFix.js for what
// this does and why. Applied once, as early as possible, before first render.
initDesktopZoomFix();

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A genuine authentication failure (bad/expired/missing token) — the ONLY
// case that should ever clear the token and bounce to /login. Everything
// else (network drop, timeout, CORS preflight hiccup, a transient 5xx while
// the backend restarts/redeploys) must NOT log a user out of a perfectly
// valid session.
const isGenuineAuthFailure = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  return status === 401 || (status === 403 && detail === 'Not authenticated');
};

const AUTH_CHECK_MAX_RETRIES = 2;
const AUTH_CHECK_RETRY_DELAY_MS = 1500;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authCheckError, setAuthCheckError] = useState(false);
  const [impersonatedAs, setImpersonatedAs] = useState(() => sessionStorage.getItem('_impersonated_as'));
  // Which of the two UIs to show: a Limited Access user always gets the Limited UI; a super admin can switch between the Main UI and
  // the Limited UI for testing (Settings > Dashboard V3). See contexts/DashboardV3Context.jsx.
  const { effectiveLimited } = useDashboardV3Test(user);

  const exitImpersonation = () => {
    const adminToken = sessionStorage.getItem('_admin_token');
    if (!adminToken) return;
    localStorage.setItem('token', adminToken);
    sessionStorage.removeItem('_admin_token');
    sessionStorage.removeItem('_impersonated_as');
    setImpersonatedAs(null);
    window.location.href = '/settings?tab=permissions';
  };

  // Switch the active session to another user (super admin "Login As") WITHOUT
  // a hard page reload — a full `window.location.href` reload forces a brand
  // new page load that re-runs the entire CORS/preflight handshake against the
  // API, which on production's custom domain can intermittently fail; when it
  // does, `checkAuth()`'s catch-all wipes the token and bounces straight back
  // to /login, which looks exactly like "Login As just logs me out". Updating
  // auth state in-memory (same as a normal login) avoids that reload entirely.
  const handleImpersonate = (token, targetUser) => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) sessionStorage.setItem('_admin_token', currentToken);
    sessionStorage.setItem('_impersonated_as', targetUser.full_name || targetUser.email);
    localStorage.setItem('token', token);
    setUser(targetUser);
    setIsAuthenticated(true);
    setImpersonatedAs(targetUser.full_name || targetUser.email);
    window.dispatchEvent(new Event('c30:auth-changed'));
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (attempt = 0) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setUser(response.data);
      setIsAuthenticated(true);
      setAuthCheckError(false);
      setLoading(false);
    } catch (error) {
      if (isGenuineAuthFailure(error)) {
        console.error('Auth check failed — session is genuinely invalid, logging out:', error);
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // Temporary/backend/network error (timeout, no response at all, or a
      // 5xx) — retry a couple of times with a short backoff before giving up.
      // Never clear the token for this; a redeploy/CORS hiccup/dropped
      // connection must never look like "you've been logged out".
      console.error(`Auth check failed on attempt ${attempt + 1} (temporary error, will retry):`, error);
      if (attempt < AUTH_CHECK_MAX_RETRIES) {
        setTimeout(() => checkAuth(attempt + 1), AUTH_CHECK_RETRY_DELAY_MS * (attempt + 1));
      } else {
        setAuthCheckError(true);
        setLoading(false);
      }
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    // Tell the ZohoSessionProvider (and any other listeners) to refetch
    // user-scoped status now that we have a fresh auth token. Without this,
    // a provider that mounted on the /login page would keep its anonymous
    // 401 fallback forever and the notification bars would never appear.
    window.dispatchEvent(new Event('c30:auth-changed'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (authCheckError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="auth-check-error-screen">
        <div className="text-center max-w-sm">
          <div className="text-lg font-semibold text-gray-800 mb-2">Can't reach the server</div>
          <p className="text-sm text-gray-500 mb-4">
            Your session is fine — we just couldn't confirm it right now. Check your connection and try again.
          </p>
          <button
            data-testid="auth-check-retry-button"
            onClick={() => {
              setAuthCheckError(false);
              setLoading(true);
              checkAuth(0);
            }}
            className="px-5 py-2 rounded-full bg-[#ee7203] text-white font-medium hover:bg-[#d66302] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="c30-theme">
      <BranchProvider user={user}>
        <ZohoSessionProvider>
        <div className="App">
          <BrowserRouter>
            <UIPreviewProvider user={user}>
            <DashboardV3Provider user={user}>
            <ActivityBarProvider user={user}>
            <TransportReturnProvider>
            <VersionCheckWrapper />
            <ToasterWrapper />
            <InstallPrompt />
            <ZohoLoginGateModal />
            {/* The host that reopens windows from Open / Recents now lives inside V3MainGate (so V3 windows get the V3 look); only the Limited UI needs it here. */}
            {effectiveLimited && <ActivityModalHost />}
            <DashboardV3Tag />
            {/* Impersonation exit banner */}
            {impersonatedAs && (
              <div
                style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: '#7c3aed', color: '#fff' }}
                className="flex items-center justify-between px-5 py-2.5 shadow-lg"
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: 20 }}>
                    Viewing as <strong>{impersonatedAs}</strong>
                  </span>
                  <span className="text-purple-200 text-xs">— you are in impersonation mode</span>
                </div>
                <button
                  onClick={exitImpersonation}
                  style={{ background: '#fff', color: '#7c3aed', fontWeight: 700, fontSize: 13, padding: '4px 16px', borderRadius: 8 }}
                >
                  Exit View
                </button>
              </div>
            )}
            {effectiveLimited ? (
              <LimitedRoutes
                user={user}
                onLogout={handleLogout}
                onUserUpdate={setUser}
                onImpersonate={handleImpersonate}
              />
            ) : (
<V3MainGate user={user} onLogout={handleLogout}>
            <Routes>
          {/* Limited Access users live under /limited; anyone else who lands here goes home. */}
          <Route path="/limited" element={<Navigate to="/" replace />} />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to={new URLSearchParams(window.location.search).get('returnTo') || "/"} replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <ErrorBoundary>
                  <HomeGate user={user} onLogout={handleLogout} />
                </ErrorBoundary>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/transport"
            element={
              isAuthenticated ? (
                <ErrorBoundary>
                    <TransportUIGate user={user} onLogout={handleLogout} />
                  </ErrorBoundary>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Settings user={user} onUserUpdate={setUser} onImpersonate={handleImpersonate} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/editor"
            element={
              isAuthenticated ? (
                <Editor user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/photo-optimization"
            element={
              isAuthenticated ? (
                <PhotoOptimization user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/transport-auto-save-docs"
            element={
              isAuthenticated ? (
                <TransportAutoSaveDocsV2 user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Reports removed per GDPR requirements */}
          <Route path="/reports-hub" element={user && (user.can_access_reports || user.role === 'super_admin') ? <ReportsHub user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="/reports" element={<Navigate to="/" replace />} />
          <Route
            path="/company-car"
            element={
              isAuthenticated ? (
                <CompanyCarHub user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/areas"
            element={
              isAuthenticated ? (
                <Areas user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/mileage-tracker/*"
            element={
              isAuthenticated ? (
                <MileageTracker user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/staffology"
            element={
              isAuthenticated && (user?.can_access_staffology || user?.role === 'super_admin') ? (
                <StaffologyPortal user={user} />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/group-absence-calendar"
            element={
              isAuthenticated && (user?.can_access_al_button || user?.role === 'super_admin') ? (
                <GroupAbsenceCalendar user={user} onLogout={handleLogout} />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/crm"
            element={
              isAuthenticated && (user?.can_access_crm || user?.role === 'super_admin') ? (
                <CRMPortal user={user} />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/hub"
            element={
              isAuthenticated ? (
                <HomeGate user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/sales" element={user && (user.can_access_sales_dashboard || user.can_access_sales || user.role === 'super_admin') ? <SalesDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="/sales-dashboard" element={user && (user.can_access_sales_dashboard || user.can_access_sales || user.role === 'super_admin') ? <SalesDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="/projects" element={user && (user.can_access_projects || user.can_access_sales_dashboard || user.sales_dashboard_projects !== false || user.role === 'super_admin') ? <Projects user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          {/* Customer Management removed per GDPR requirements */}
          <Route path="/customer-management" element={<Navigate to="/" replace />} />
          {/* Budget removed per GDPR requirements */}
          <Route path="/budget" element={<Navigate to="/" replace />} />
          {/* Sales Tracker */}
          <Route path="/sales-tracker" element={user && (user.can_view_sales_tracker || user.role === 'super_admin') ? <SalesTracker user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route path="/error-log" element={user && (user.can_view_error_log || user.role === 'super_admin') ? <ErrorLog user={user} /> : <Navigate to="/" replace />} />
          <Route path="/pi-counts" element={user && (user.can_access_pi_counts || user.role === 'super_admin') ? <PICounts user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/pi-counts/reports/:reportId" element={user && (user.can_access_pi_reports || user.role === 'super_admin') ? <PIReportViewer user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/pi-counts/:countId" element={user && (user.can_access_pi_counts || user.role === 'super_admin') ? <PICountDetail user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/pi-counts/discrepancy/:reportId" element={user && (user.can_access_pi_counts || user.role === 'super_admin') ? <DiscrepancyReport user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/pi-counts/adjustment/:reportId" element={user && (user.can_access_pi_counts || user.role === 'super_admin') ? <AdjustmentReport user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/pi-counts-warehouse" element={user && (user.can_view_pi_warehouse || user.role === 'super_admin') ? <PICountsWarehouse user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/goods-in" element={user && (user.can_access_goods_in || user.role === 'super_admin') ? <GoodsIn user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/goods-in-warehouse" element={user && (user.can_access_goods_in_warehouse || user.role === 'super_admin') ? <GoodsInWarehouse user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/cross-deployment-backup" element={user ? <CrossDeploymentBackup user={user} /> : <Navigate to="/login" replace />} />
          <Route path="/m45-hub" element={user && (user.can_access_m45 !== false || user.role === 'super_admin') ? <M45Hub user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          {/* Pro Forma & Quotes now modal-only - redirect direct URL access to home */}
          <Route path="/proforma" element={<Navigate to="/" replace />} />
          <Route path="/proforma/*" element={<Navigate to="/" replace />} />
          <Route path="/quotes" element={<Navigate to="/" replace />} />
          <Route path="/quotes/*" element={<Navigate to="/" replace />} />
          <Route path="/stock-transfer" element={user && (user.can_access_stock_transfer !== false || user.role === 'super_admin') ? <StockTransfer user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/stock-transfer/:transferId" element={user && (user.can_access_stock_transfer !== false || user.role === 'super_admin') ? <StockTransfer user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/b29-stock-transfer" element={user && (user.can_access_stock_transfer !== false || user.role === 'super_admin') ? <B29StockTransfer user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/po-expenses" element={user && (user.can_access_po_expenses !== false || user.role === 'super_admin') ? <POExpenses user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/manage-calls" element={user ? <ManageCallsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
          <Route path="/sales-hub" element={user ? <SalesHubPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
          <Route path="/project-tracking" element={user && (user.role === 'super_admin' || user.can_view_project_tracking) ? <ProjectTrackingPage user={user} onLogout={handleLogout} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/spellers" element={user && (user.role === 'super_admin' || user.can_view_project_tracking) ? <SpellersPage user={user} onLogout={handleLogout} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/spellers/:id" element={user && (user.role === 'super_admin' || user.can_view_project_tracking) ? <SpellersProjectDetail user={user} onLogout={handleLogout} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/bi-reports" element={user && (user.role === 'super_admin' || user.can_access_bi_reports) ? <BIReportsPage user={user} onLogout={handleLogout} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/price-increases" element={user && (user.can_access_po_expenses !== false || user.role === 'super_admin') ? <PriceIncreases user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/stock-management" element={user && (user.can_access_stock_management || user.role === 'super_admin') ? <StockManagement user={user} /> : user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
          <Route path="/automations" element={user ? <AutomationsDashboard user={user} /> : <Navigate to="/login" replace />} />
          <Route path="/sdp-crm" element={user ? <SDPCRMPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
          {/* Interview Notes is now a modal in Tools dropdown - redirect old routes */}
          <Route path="/tools/interview-notes" element={<Navigate to="/" replace />} />
          <Route path="/tools/interview-notes/:noteId" element={<Navigate to="/" replace />} />
          {/* Files removed per GDPR requirements */}
          <Route path="/files" element={<Navigate to="/" replace />} />
          <Route
            path="/oauth/authorize"
            element={
              isAuthenticated ? (
                <ErrorBoundary><OAuthConsent user={user} /></ErrorBoundary>
              ) : (
                <Navigate to={`/login?returnTo=${encodeURIComponent('/oauth/authorize' + window.location.search)}`} replace />
              )
            }
          />
        </Routes>
            </V3MainGate>
            )}
            </TransportReturnProvider>
            </ActivityBarProvider>
            </DashboardV3Provider>
            </UIPreviewProvider>
      </BrowserRouter>
    </div>
    </ZohoSessionProvider>
    </BranchProvider>
    </ThemeProvider>
  );
}

// Wrapper to use useLocation inside BrowserRouter
function ToasterWrapper() {
  const location = useLocation();
  
  // Determine toast position based on current route
  const getToastPosition = () => {
    const path = location.pathname;
    
    // Footer position for Edit/View/Areas pages (bottom-center, above Safari bar on iPad)
    if (path.includes('/transport') || path.includes('/areas')) {
      return { position: 'bottom-center', offset: '100px' };
    }
    
    // Between dashboard and settings on Hub (top-right)
    if (path === '/' || path === '/hub') {
      return { position: 'top-right', offset: '80px' };
    }
    
    // Default top-center for other pages
    return { position: 'top-center', offset: '80px' };
  };
  
  const { position, offset } = getToastPosition();
  
  return (
    <Toaster 
      position={position}
      offset={offset}
      duration={2000}
      closeButton={true}
      richColors={true}
      toastOptions={{
        style: {
          pointerEvents: 'auto',
        },
      }}
    />
  );
}

// Version check wrapper - shows banner when new deployment is detected
function VersionCheckWrapper() {
  const { newVersionAvailable, forceRefresh, dismissNotification } = useVersionCheck();
  
  if (!newVersionAvailable) return null;
  
  return (
    <VersionUpdateBanner 
      onRefresh={forceRefresh} 
      onDismiss={dismissNotification} 
    />
  );
}

export default App;
